import React, { useEffect, useRef, useState } from "react";
import CapsuleCard from "./CapsuleCard";
import { exercisesByMood } from "../../data/exerciseCatalog";
import { createSession } from "../../services/sessionService";

const moodGradients = {
  1: "linear-gradient(135deg, hsl(252, 91%, 73%), hsl(297, 17%, 56%))",
  2: "linear-gradient(135deg, #c447c4, hsl(290, 15%, 65%))",
  3: "linear-gradient(135deg, hsl(350, 93%, 66%), hsl(0, 26%, 64%))",
  4: "linear-gradient(135deg, hsl(96, 60%, 46%), hsl(200, 34%, 62%))",
  5: "linear-gradient(135deg, #c447c4, hsl(290, 15%, 65%))",
  6: "linear-gradient(135deg, hsl(96, 60%, 46%), hsl(200, 34%, 62%))",
  7: "linear-gradient(135deg, hsl(96, 60%, 46%), hsl(200, 34%, 62%))",
  8: "linear-gradient(135deg, #c447c4, hsl(290, 15%, 65%))",
  9: "linear-gradient(135deg, hsl(252, 91%, 73%), hsl(297, 17%, 56%))",
};

// Props:
//   mood            — mood object from getMoods() API { id, name, label, icon }
//   onClose         — exit without starting
//   onStartExercise — ({ exercise, sessionId }) called when user clicks "Start Capsule"

const CapsuleRevealScreen = ({ mood, onClose, onStartExercise }) => {
  const [isRevealing, setIsRevealing] = useState(true);
  const [showGlow, setShowGlow] = useState(false);
  const [currentCapsule, setCurrentCapsule] = useState(null);
  const [starting, setStarting] = useState(false);
  const pickedExerciseRef = useRef(null);

  const moodName = (mood.name || mood.label || "").toLowerCase();
  const pool = exercisesByMood[moodName] || [];

  // Pick a random exercise, optionally excluding one ID (reroll no-repeat).
  const pickExercise = (excludeId = null) => {
    const candidates =
      pool.length > 1 && excludeId
        ? pool.filter((ex) => ex.id !== excludeId)
        : pool;
    return candidates[Math.floor(Math.random() * candidates.length)];
  };

  const revealExercise = (excludeId = null) => {
    setIsRevealing(true);
    setShowGlow(false);

    if (pool.length === 0) {
      console.warn("No exercises found for mood:", moodName);
      setIsRevealing(false);
      return;
    }

    const exercise = pickExercise(excludeId);
    pickedExerciseRef.current = exercise;

    const capsuleData = {
      capsule_id: exercise.id,
      capsule_name: exercise.title,
      capsule_description: exercise.subtitle,
    };

    const spinTimer = setTimeout(() => setShowGlow(true), 300);
    const revealTimer = setTimeout(() => {
      setCurrentCapsule(capsuleData);
      setIsRevealing(false);
      setShowGlow(false);
    }, 1500);

    return () => {
      clearTimeout(spinTimer);
      clearTimeout(revealTimer);
    };
  };

  useEffect(() => {
    return revealExercise();
  }, [mood.id]);

  const rollAgainHandler = () => {
    revealExercise(pickedExerciseRef.current?.id);
  };

  const startCapsuleHandler = async () => {
    if (!currentCapsule || !pickedExerciseRef.current || starting) return;
    setStarting(true);

    const exercise = pickedExerciseRef.current;
    let sessionId = null;

    try {
      const result = await createSession({
        mood: moodName,
        capsuleName: exercise.title,
        activity: exercise.template,
      });
      sessionId = result.sessionId;
    } catch (err) {
      console.error("createSession failed, proceeding without session:", err);
    }

    onStartExercise({ exercise, sessionId });
  };

  const showReroll = pool.length > 1;

  return (
    <div className="tw-fixed tw-inset-0 tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4 md:tw-p-12 tw-overflow-auto tw-bg-orange-gradient">
      {/* Preload all emojis used in this flow to prime browser glyph cache */}
      <span aria-hidden="true" style={{ position: "absolute", opacity: 0, pointerEvents: "none", fontSize: 0 }}>
        💊✨⏳🎖️🌸
      </span>
      <button
        onClick={onClose}
        className="tw-absolute tw-top-4 tw-left-4 md:tw-top-10 md:tw-left-14 tw-text-2xl md:tw-text-4xl tw-text-white hover:tw-text-gray-200"
      >
        ✕
      </button>

      <h1 className="tw-mb-2 tw-text-2xl md:tw-text-4xl tw-font-bold tw-text-white tw-text-center">
        Here's your capsule for today
      </h1>
      <p className="tw-text-center tw-text-base tw-font-normal tw-text-white/80">
        Perfectly matched to your current energy
      </p>

      <div className="flip-wrapper tw-mt-2 tw-relative">
        {showGlow && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none">
            <div className="tw-absolute tw-w-56 tw-h-56 tw-rounded-full tw-bg-white/40 tw-blur-2xl tw-animate-glow-core" />
            <div className="tw-absolute tw-w-64 tw-h-64 tw-rounded-full tw-bg-yellow-300/30 tw-blur-3xl tw-animate-glow-aura" />
            <div className="tw-absolute tw-w-40 tw-h-40 tw-rounded-full tw-bg-amber-200/30 tw-blur-xl tw-animate-glow-pulse" />
          </div>
        )}

        <div className={`flip-card ${!isRevealing ? "flipped" : ""}`}>
          <div className="flip-face">
            <div className="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
              <div className="tw-relative tw-z-10 tw-w-60 tw-h-60 tw-rounded-full tw-bg-white/20 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center">
                <div className="tw-text-7xl tw-animate-spin">💊</div>
              </div>
            </div>
          </div>

          <div className="flip-face flip-back">
            {currentCapsule && (
              <CapsuleCard
                gradient={moodGradients[mood.id] || moodGradients[1]}
                emoji="✨"
                title={currentCapsule.capsule_name}
                description={currentCapsule.capsule_description}
                isRevealed={true}
              />
            )}
          </div>
        </div>
      </div>

      <div className="tw-flex tw-justify-center tw-mt-8 tw-gap-2">
        <p className="tw-text-2xl tw-text-white">⏳</p>
        <p className="tw-text-lg tw-font-medium tw-text-white">
          {pickedExerciseRef.current?.duration || "2 min"} experience
        </p>
      </div>

      <div className="tw-flex tw-justify-center tw-gap-3 tw-mt-3">
        <button
          className="tw-px-8 md:tw-px-14 tw-py-2 tw-text-base tw-rounded-3xl tw-bg-[#FA7415] tw-backdrop-blur-sm tw-border-[0.5px] tw-border-solid tw-border-white hover:tw-bg-[#FA7415]/90 tw-text-white hover:tw-scale-105 tw-transform tw-transition-transform disabled:tw-opacity-50"
          onClick={startCapsuleHandler}
          disabled={starting || !currentCapsule}
        >
          {starting ? "Starting..." : "Start Capsule"}
        </button>

        {showReroll && (
          <button
            className="tw-px-6 tw-py-2 tw-text-sm tw-rounded-3xl tw-bg-white/10 tw-backdrop-blur-sm tw-border-[0.5px] tw-border-solid tw-border-white hover:tw-bg-white/20 tw-text-white hover:tw-scale-105 tw-transform tw-transition-transform"
            onClick={rollAgainHandler}
          >
            Try another
          </button>
        )}
      </div>

      <div className="tw-pt-4 tw-text-[14px] tw-text-white/70 tw-flex tw-flex-col tw-text-center">
        <span>
          This capsule was chosen based on your current mood and energy.
        </span>
        <span>
          Each one is designed to help you feel better in just a few minutes.
        </span>
      </div>
    </div>
  );
};

export default CapsuleRevealScreen;
